/**
 * Hand in hand in the mother tongue (D-122): under any help text (instruction or feedback of an
 * exercise, correction of a mock exam, AI feedback, home tips, method, tutor), « Traduire dans ma
 * langue » and « Écouter ». The server translates a text once per language and keeps it; the texts
 * of a screen leave in one request (@agencyos/core `createTextBatcher`). With « Toujours afficher
 * dans ma langue », the translation appears by itself under the original. Never during a timed
 * exam section: the server refuses and the page says so; the original always stays on screen.
 */

import { academyMotherTongueListenCreate, academyMotherTongueTextsCreate, useAcademyMePartialUpdate, useAcademyMeRetrieve } from "@agencyos/api-client";
import {
  type SpokenLanguage,
  type TextKind,
  type TextSource,
  type TranslatedText,
  createTextBatcher,
  languageLabel,
  needsTranslation,
  parseListen,
} from "@agencyos/core";
import { Badge } from "@agencyos/ui-web";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Headphones, Languages, Square } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { stopSpeaking } from "../audio";
import { Markdown } from "./Markdown";
import { useLanguages } from "./MotherTongue";
import { playExplanation } from "./voice";

const POLL_MS = 2500;
const POLL_MAX = 24;

const ask = createTextBatcher((body) => academyMotherTongueTextsCreate(body));

/** The learner's mother tongue and the « always show » preference (empty outside the Academy). */
export function useMotherTongue(): { code: string; always: boolean; language: SpokenLanguage | undefined } {
  const me = useAcademyMeRetrieve({ query: { retry: false, staleTime: 60_000 } });
  const languages = useLanguages();
  const data = me.data?.status === 200 ? me.data.data : null;
  const code = data?.mother_tongue ?? "";
  return { code, always: Boolean(data?.always_mother_tongue), language: languages.find((l) => l.code === code) };
}

/** The kept translation of one text (asked again while it is being made). */
export function useTextTranslation(text: string, source: TextSource, kind: TextKind, enabled: boolean, examLanguage?: TextSource) {
  const { code } = useMotherTongue();
  const on = enabled && Boolean(text.trim()) && needsTranslation(code, source);
  return useQuery<TranslatedText>({
    queryKey: ["mother-tongue-text", code, source, kind, examLanguage ?? "", text],
    queryFn: () => ask({ text, source, kind, language: code, examLanguage }),
    enabled: on,
    retry: false,
    staleTime: (q) => (q.state.data?.status === "ready" ? Infinity : 0),
    refetchInterval: (q) => (q.state.data?.status === "pending" && q.state.dataUpdateCount < POLL_MAX ? POLL_MS : false),
  });
}

interface Props {
  /** The original text (shown by the page above this block). */
  text: string;
  /** Its language: the interface language for a help, the exam language for an exam text. */
  source: TextSource;
  kind?: TextKind;
  /** The language of the exam prepared: its words and examples stay as they are. */
  examLanguage?: TextSource;
  className?: string;
}

/** « Traduire dans ma langue » + « Écouter » under a help text; nothing without a mother tongue. */
export function InMyLanguage({ text, source, kind = "help", examLanguage, className }: Props) {
  const { t, i18n } = useTranslation();
  const { code, always, language } = useMotherTongue();
  const [asked, setAsked] = useState(false);
  const [listening, setListening] = useState(false);
  const [note, setNote] = useState("");
  const audio = useRef<HTMLAudioElement>(null);
  const shown = asked || always;
  const query = useTextTranslation(text, source, kind, shown, examLanguage);
  useEffect(() => () => stopSpeaking(), []);
  if (!text.trim() || !needsTranslation(code, source)) return null;
  const value = query.data;

  const listen = async () => {
    if (!value?.key) return;
    setListening(true);
    setNote("");
    try {
      let plan = parseListen((await academyMotherTongueListenCreate({ key: value.key, language: code })).data);
      if (plan.mode === "server" && plan.status === "pending") {
        setNote(t("academyCourse.listen.preparing"));
        plan = { ...plan, mode: "device" };
      }
      const voice = await playExplanation(plan, audio.current);
      if (voice === "none") setNote([plan.message, t("academyCourse.inMyLanguage.cannotSpeak")].filter(Boolean).join(" "));
      else setNote(plan.message || (voice === "server" ? t("academyCourse.listen.server") : voice === "fallback" ? t("academyCourse.inMyLanguage.noVoice") : ""));
    } catch {
      setNote(t("academyCourse.inMyLanguage.unavailable"));
    } finally {
      setListening(false);
    }
  };

  const stop = () => {
    stopSpeaking();
    audio.current?.pause();
    setListening(false);
  };

  const status = value?.status;
  const notes: Record<string, string> = {
    pending: t("academyCourse.inMyLanguage.pending"),
    unavailable: value?.message || t("academyCourse.inMyLanguage.unavailable"),
    limit: value?.message || t("academyCourse.inMyLanguage.limit"),
    too_long: t("academyCourse.inMyLanguage.tooLong"),
    blocked: t("academyCourse.inMyLanguage.blocked"),
  };
  const label = language ? languageLabel(language, i18n.language) : code;
  return (
    <div className={["ac-inmylang", className].filter(Boolean).join(" ")}>
      <div className="ac-inmylang__actions">
        {!shown ? (
          <button type="button" className="ac-inmylang__button" onClick={() => setAsked(true)}>
            <Languages aria-hidden size={14} />
            <span>{t("academyCourse.inMyLanguage.action")}</span>
          </button>
        ) : (
          !always && (
            <button type="button" className="ac-inmylang__button" onClick={() => setAsked(false)}>
              <Languages aria-hidden size={14} />
              <span>{t("academyCourse.inMyLanguage.hide")}</span>
            </button>
          )
        )}
        {shown && status === "ready" && (
          <button type="button" className="ac-inmylang__button" onClick={() => void (listening ? stop() : listen())}>
            {listening ? <Square aria-hidden size={13} /> : <Headphones aria-hidden size={14} />}
            <span>{listening ? t("academyCourse.listen.stop") : t("academyCourse.inMyLanguage.listen")}</span>
          </button>
        )}
      </div>
      {shown && (
        <div className="ac-inmylang__body" aria-live="polite">
          {status === "ready" && value ? (
            <div lang={code} dir={language?.rtl ? "rtl" : undefined}>
              <div className="ac-inmylang__head">
                <Badge tone="info">{t("academyCourse.inMyLanguage.badge")}</Badge>
                {value.beta && <Badge tone="warning">{t("academyCourse.translate.beta")}</Badge>}
                <span className="ac-caption">{label}</span>
              </div>
              <Markdown source={value.text} lang={code} />
              {value.beta && value.message && <p className="ac-footnote">{value.message}</p>}
            </div>
          ) : (
            <p className="ac-footnote" role="status">
              {query.isFetching && !value ? t("academyCourse.inMyLanguage.pending") : status ? (notes[status] ?? "") : ""}
            </p>
          )}
        </div>
      )}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audio} hidden onEnded={() => setListening(false)} />
      {note && (
        <p className="ac-footnote" role="status">
          {note}
        </p>
      )}
    </div>
  );
}

/** « Toujours afficher dans ma langue », saved on the learner (web and phone). */
export function AlwaysInMyLanguage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { code, always } = useMotherTongue();
  const save = useAcademyMePartialUpdate({ mutation: { onSuccess: () => void queryClient.invalidateQueries() } });
  const id = useId();
  if (!code) return null;
  return (
    <div className="ac-inmylang__always">
      <input id={id} type="checkbox" checked={always} onChange={(event) => save.mutate({ data: { always_mother_tongue: event.target.checked } })} />
      <span>
        <label htmlFor={id}>{t("academyCourse.inMyLanguage.always")}</label>
        <span className="ac-footnote">{t("academyCourse.inMyLanguage.alwaysHint")}</span>
      </span>
    </div>
  );
}

/** The language of the interface (French or English): the language of the AI's own feedback. */
export function useUiSource(): TextSource {
  const { i18n } = useTranslation();
  return i18n.language.startsWith("en") ? "en" : "fr";
}

/** The AI feedback on a writing or a speaking (comment + points to improve), in the mother tongue. */
export function FeedbackInMyLanguage({ comment, errors, examLanguage }: { comment: string; errors: { excerpt?: string; correction?: string; explanation?: string }[]; examLanguage: TextSource }) {
  const source = useUiSource();
  const points = errors.filter((e) => e.explanation).map((e) => `- ${e.excerpt ?? ""} → ${e.correction ?? ""} : ${e.explanation ?? ""}`);
  const text = [comment, ...points].filter(Boolean).join("\n");
  return <InMyLanguage text={text} source={source} examLanguage={examLanguage} kind="ai_feedback" />;
}
